import {
  TabbedForm,
  SelectInput,
  TabbedFormViewProps,
  BooleanInput,
  BooleanField,
  SelectField,
  Labeled,
  FormDataConsumer,
  required,
  minValue,
  maxValue,
  ReferenceManyField,
  DataTable,
  NumberInput,
  useCanAccess,
} from 'react-admin';
import { Divider } from '@mui/material';
import {
  BILLING_TYPE_CHOICES,
  MAIL_CAP_STATUS_CHOICES,
  PAYMENT_TYPE_CHOICES,
  normalizeAbn,
  validateAbn,
} from '../utils';
import { ReactElement } from 'react';
import TzDateField from '../../../Components/TzDate/TzDateField';
import { CurrencyAmountInput } from '../../../Components/CurrencyAmount/CurrencyAmountInput';
import InfoLabel from '../../../Components/InfoLabel';
import RecalculateBudgetStatusButton from '../RecalculateBudgetStatusButton';
import SyncWithNetSuiteButton from '../SyncWithNetSuiteButton';
import UnresolvedNetSuiteCustomerSyncErrors from './UnresolvedNetSuiteCustomerSyncErrors';
import { DeepPartial } from '../../../utils';
import { MerchantResource } from '../../../Entities/Merchant';

/**
 * Maximum monthly budget that a merchant can have. Currently set to $9,999,999.
 */
export const MAX_MONTHLY_BUDGET = 9999999;
const CPA_BILLING_TYPE_ID = BILLING_TYPE_CHOICES.find((v) => v.name === 'CPA')?.id;

interface BillingInfoTabContentProps {
  canListTrackedErrors: boolean;
  canWriteClicks: boolean;
  canWriteMerchants: boolean;
  formData?: DeepPartial<MerchantResource>;
}

export default function BillingInfoTab(props: TabbedFormViewProps): ReactElement {
  const merchantWriteAccess = useCanAccess({ resource: 'merchants', action: 'write' });
  const clickWriteAccess = useCanAccess({ resource: 'clicks', action: 'write' });
  const trackedErrorListAccess = useCanAccess({
    resource: 'tracked_error_issues',
    action: 'list',
  });
  const canWriteMerchants = merchantWriteAccess.canAccess ?? false;
  const canWriteClicks = clickWriteAccess.canAccess ?? false;
  const canListTrackedErrors = trackedErrorListAccess.canAccess ?? false;

  return (
    <TabbedForm.Tab label="Billing Info" {...props}>
      <FormDataConsumer>
        {({ formData }) => (
          <BillingInfoTabContent
            canListTrackedErrors={canListTrackedErrors}
            canWriteClicks={canWriteClicks}
            canWriteMerchants={canWriteMerchants}
            formData={formData}
          />
        )}
      </FormDataConsumer>
    </TabbedForm.Tab>
  );
}

function BillingInfoTabContent({
  canListTrackedErrors,
  canWriteClicks,
  canWriteMerchants,
  formData,
}: BillingInfoTabContentProps): ReactElement {
  const merchantId = formData?.id;
  const isCreateScreen = merchantId == null || merchantId === '';
  const isCPAMerchant = formData?.attributes?.billing_type_code === CPA_BILLING_TYPE_ID;

  return (
    <>
      <CurrencyAmountInput
        defaultValue={MAX_MONTHLY_BUDGET}
        label="Monthly Budget (AUD)"
        source="attributes.monthly_budget"
        sx={isCPAMerchant ? { display: 'none' } : undefined}
        validate={[
          required(),
          minValue(1, 'Merchant must have a budget of at least $1'),
          maxValue(
            MAX_MONTHLY_BUDGET,
            'Unexpectedly high budget; if a merchant really could want to spend more than $10 million dollars, please reach out to dev to increase this limit',
          ),
        ]}
        // If the merchant is a CPA merchant, switch this component from an
        // uncontrolled input into a controlled one and force the value to be
        // the default value. This is to ensure that admins are not able to mess
        // with the budget of CPA merchants as we're not able to track the
        // budget for CPA merchants anyhow.
        value={isCPAMerchant ? MAX_MONTHLY_BUDGET : undefined}
      />
      <Labeled>
        <BooleanField
          record={formData}
          label="Over Budget?"
          source="attributes.over_budget"
          looseValue
        />
      </Labeled>
      <Labeled>
        <SelectField
          record={formData}
          label="Mail Cap Status"
          choices={MAIL_CAP_STATUS_CHOICES}
          source="attributes.mail_cap_status_code"
        />
      </Labeled>
      {!isCreateScreen && canWriteMerchants && canWriteClicks && (
        <RecalculateBudgetStatusButton merchantId={merchantId} />
      )}
      <SelectInput
        choices={BILLING_TYPE_CHOICES}
        label="Billing Type"
        source="attributes.billing_type_code"
        isRequired
        validate={required()}
        defaultValue={BILLING_TYPE_CHOICES[0].id}
      />
      <SelectInput
        choices={PAYMENT_TYPE_CHOICES}
        label="Payment Type"
        source="attributes.payment_type_code"
      />
      <BooleanInput label="Is Foreign Company?" source="attributes.is_foreign_company" />
      <NumberInput
        label={<InfoLabel label="ABN" description="Australian Business Number" />}
        source="attributes.abn"
        format={normalizeAbn}
        validate={validateAbn}
      />
      {!isCreateScreen && (
        <>
          <Divider flexItem sx={{ mb: 2 }} />
          <Labeled label="Pay Accounts" sx={{ width: '100%' }}>
            <ReferenceManyField reference="pay_accounts" target="merchant.id">
              <DataTable bulkActionButtons={false} rowClick={false} sx={{ width: '100%' }}>
                <DataTable.Col source="attributes.netsuite_id" label="NetSuite Customer ID" />
                <DataTable.Col source="attributes.secure_pay_token" label="SecurePay Payor ID" />
                <DataTable.Col
                  source="attributes.netsuite_last_error"
                  label="Last error from NetSuite"
                />
                <DataTable.Col
                  source="attributes.payment_method_expiry_date"
                  label="Payment Method Expiry Date"
                >
                  <TzDateField source="attributes.payment_method_expiry_date" />
                </DataTable.Col>
              </DataTable>
            </ReferenceManyField>
          </Labeled>
          <Divider sx={{ mb: 2 }} />
          {canWriteMerchants && <SyncWithNetSuiteButton merchantId={merchantId} />}
          {canListTrackedErrors && <UnresolvedNetSuiteCustomerSyncErrors merchantId={merchantId} />}
        </>
      )}
    </>
  );
}
